import React, { useState } from 'react';
import { UserIcon, SettingsIcon, BellIcon, ShieldIcon, LogOutIcon, HelpCircleIcon, QrCodeIcon, MessageSquareIcon, CoffeeIcon, CameraIcon, XIcon, ChevronRightIcon, AlertTriangleIcon, InfoIcon, LockIcon, KeyIcon, EyeIcon, FileTextIcon, DownloadIcon, TrashIcon } from 'lucide-react';
import GlassCard from '../ui/GlassCard';
import Button from '../ui/Button';
import { useHydration } from '../../contexts/HydrationContext';
interface ProfileProps {
  onToggleAdminMode?: () => void;
}
const Profile: React.FC<ProfileProps> = ({
  onToggleAdminMode
}) => {
  const {
    userProfile,
    bottlesSaved,
    streakDays
  } = useHydration();
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const handleMenuItemClick = (id: string) => {
    if (id === 'admin' && onToggleAdminMode) {
      onToggleAdminMode();
    } else if (id === 'settings') {
      setShowSettingsModal(true);
    } else if (id === 'notifications') {
      setShowNotificationsModal(true);
    } else if (id === 'privacy') {
      setShowPrivacyModal(true);
    } else if (id === 'help') {
      setShowHelpModal(true);
    } else if (id === 'logout') {
      setShowLogoutConfirm(true);
    }
  };
  const menuItems = [{
    id: 'notifications',
    icon: BellIcon,
    label: 'Notifications',
    badge: '3'
  }, {
    id: 'privacy',
    icon: ShieldIcon,
    label: 'Privacy & Data'
  }, {
    id: 'help',
    icon: HelpCircleIcon,
    label: 'Help & Support'
  }, {
    id: 'logout',
    icon: LogOutIcon,
    label: 'Log Out',
    danger: true
  }];
  return <div className="h-full bg-[#1A237E] pt-12 pb-24 px-4 overflow-y-auto" data-id="element-1088">
      <header className="mb-6" data-id="element-1089">
        <h1 className="text-2xl font-bold text-white font-gt-ultra" data-id="element-1090">Profile</h1>
        <p className="text-[#00E4BB] font-gill-sans" data-id="element-1091">Manage your account</p>
      </header>
      {/* User Profile Card */}
      <div className="flex items-center mb-8" data-id="element-1092">
        <div className="w-20 h-20 bg-[#00E4BB]/20 rounded-full flex items-center justify-center mr-4 border-2 border-[#00E4BB] relative cursor-pointer group" onClick={() => handleMenuItemClick('settings')} data-id="element-1093">
          <UserIcon className="w-10 h-10 text-[#00E4BB]" data-id="element-1094" />
          <div className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity" data-id="element-1095">
            <CameraIcon className="w-8 h-8 text-white" data-id="element-1096" />
          </div>
          <div className="absolute -bottom-1 -right-1 bg-[#00E4BB] rounded-full p-1" data-id="element-1097">
            <PlusIcon className="w-4 h-4 text-[#1A237E]" data-id="element-1098" />
          </div>
        </div>
        <div data-id="element-1099">
          <h2 className="text-xl font-bold text-white" data-id="element-1100">{userProfile.name}</h2>
          <p className="text-[#00E4BB]" data-id="element-1101">Hydration Hero</p>
          <div className="flex items-center mt-1 text-sm text-gray-300" data-id="element-1102">
            <span className="mr-3" data-id="element-1103">{streakDays} day streak</span>
            <span data-id="element-1104">{bottlesSaved} bottles saved</span>
          </div>
        </div>
        <button className="ml-auto w-10 h-10 bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-full flex items-center justify-center" onClick={() => handleMenuItemClick('settings')} data-id="element-1105">
          <SettingsIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1106" />
        </button>
      </div>
      {/* User Stats */}
      <div className="grid grid-cols-3 gap-3 mb-8" data-id="element-1107">
        <GlassCard className="p-3 text-center" data-id="element-1108">
          <p className="text-xl font-bold text-white" data-id="element-1109">78</p>
          <p className="text-xs text-gray-300" data-id="element-1110">Hydration Score</p>
        </GlassCard>
        <GlassCard className="p-3 text-center" data-id="element-1111">
          <p className="text-xl font-bold text-white" data-id="element-1112">32</p>
          <p className="text-xs text-gray-300" data-id="element-1113">Day Streak</p>
        </GlassCard>
        <GlassCard className="p-3 text-center" data-id="element-1114">
          <p className="text-xl font-bold text-white" data-id="element-1115">450</p>
          <p className="text-xs text-gray-300" data-id="element-1116">Points</p>
        </GlassCard>
      </div>
      {/* QR Code Section */}
      <div className="mb-8" data-id="element-1117">
        <h2 className="text-xl font-bold text-[#FAFAFA] mb-3 font-gt-ultra" data-id="element-1118">
          Connect with Friends
        </h2>
        <GlassCard className="p-4" data-id="element-1119">
          <div className="flex items-center justify-center mb-4" data-id="element-1120">
            <div className="w-48 h-48 bg-white p-4 rounded-lg" data-id="element-1121">
              <QrCodeIcon className="w-full h-full text-[#1A237E]" data-id="element-1122" />
            </div>
          </div>
          <p className="text-center text-[#FAFAFA] text-sm" data-id="element-1123">
            Scan to connect with {userProfile.name}
          </p>
        </GlassCard>
      </div>
      {/* Friends List */}
      <div className="mb-8" data-id="element-1124">
        <h2 className="text-xl font-bold text-[#FAFAFA] mb-3 font-gt-ultra" data-id="element-1125">
          Hydration Buddies
        </h2>
        <div className="space-y-3" data-id="element-1126">
          {[{
          name: 'Sarah K.',
          status: 'Hydration Hero',
          avatar: 'https://i.pravatar.cc/100?img=1'
        }, {
          name: 'Mike R.',
          status: 'Eco Warrior',
          avatar: 'https://i.pravatar.cc/100?img=2'
        }].map((friend, index) => <GlassCard key={index} className="p-3 flex items-center" data-id="element-1127">
              <img src={friend.avatar} alt={friend.name} className="w-12 h-12 rounded-full mr-3" data-id="element-1128" />
              <div className="flex-1" data-id="element-1129">
                <h3 className="text-[#FAFAFA] font-medium" data-id="element-1130">{friend.name}</h3>
                <p className="text-sm text-[#00E4BB]" data-id="element-1131">{friend.status}</p>
              </div>
              <button className="w-8 h-8 bg-[#FAFAFA]/10 rounded-full flex items-center justify-center" data-id="element-1132">
                <MessageSquareIcon className="w-4 h-4 text-[#00E4BB]" data-id="element-1133" />
              </button>
            </GlassCard>)}
        </div>
      </div>
      {/* Menu Items */}
      <div className="space-y-3" data-id="element-1134">
        {menuItems.map(item => <GlassCard key={item.id} className={`p-4 flex items-center ${item.danger ? 'border-red-500/30' : ''} cursor-pointer`} onClick={() => handleMenuItemClick(item.id)} data-id="element-1135">
            <div className={`w-10 h-10 rounded-full ${item.danger ? 'bg-red-500/20' : 'bg-[#00E4BB]/20'} flex items-center justify-center mr-3`} data-id="element-1136">
              <item.icon className={`w-5 h-5 ${item.danger ? 'text-red-500' : 'text-[#00E4BB]'}`} data-id="element-1137" />
            </div>
            <span className={`flex-1 ${item.danger ? 'text-red-400' : 'text-white'}`} data-id="element-1138">
              {item.label}
            </span>
            {item.badge && <div className="w-6 h-6 bg-[#A6FF4D] rounded-full flex items-center justify-center" data-id="element-1139">
                <span className="text-xs font-medium text-[#002B36]" data-id="element-1140">
                  {item.badge}
                </span>
              </div>}
            <ChevronRightIcon className={`w-5 h-5 ml-2 ${item.danger ? 'text-red-400' : 'text-[#00E4BB]'}`} data-id="element-1141" />
          </GlassCard>)}
      </div>
      {/* App Version */}
      <div className="mt-8 text-center" data-id="element-1142">
        <p className="text-xs text-gray-400" data-id="element-1143">
          Smart Soda Hydrate+ v1.0.3{' '}
          <span className="cursor-pointer opacity-50 hover:opacity-100 hover:text-[#00E4BB] transition-opacity" onClick={onToggleAdminMode} data-id="element-1144">
            • Barista
          </span>
        </p>
      </div>
      {/* Settings Modal */}
      {showSettingsModal && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-1145">
          <div className="bg-[#1A237E] rounded-xl p-5 w-full max-w-md border border-[#00E4BB]/30" data-id="element-1146">
            <div className="flex justify-between items-center mb-4" data-id="element-1147">
              <h3 className="text-xl font-bold text-white" data-id="element-1148">Profile Settings</h3>
              <button className="text-gray-400 hover:text-white" onClick={() => setShowSettingsModal(false)} data-id="element-1149">
                <XIcon className="w-5 h-5" data-id="element-1150" />
              </button>
            </div>
            <div className="mb-6" data-id="element-1151">
              <div className="flex flex-col items-center justify-center mb-6" data-id="element-1152">
                <div className="w-24 h-24 bg-[#00E4BB]/20 rounded-full flex items-center justify-center mb-3 border-2 border-[#00E4BB] relative" data-id="element-1153">
                  <UserIcon className="w-12 h-12 text-[#00E4BB]" data-id="element-1154" />
                  <div className="absolute bottom-0 right-0 bg-[#00E4BB] rounded-full p-1" data-id="element-1155">
                    <CameraIcon className="w-5 h-5 text-[#1A237E]" data-id="element-1156" />
                  </div>
                </div>
                <p className="text-[#00E4BB] text-sm" data-id="element-1157">
                  Tap to update your photo
                </p>
              </div>
              <div className="space-y-4" data-id="element-1158">
                <div data-id="element-1159">
                  <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-1160">
                    Display Name
                  </label>
                  <input type="text" value={userProfile.name} className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-1161" />
                </div>
                <div data-id="element-1162">
                  <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-1163">
                    Email
                  </label>
                  <input type="email" value="user@example.com" className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-1164" />
                </div>
                <div data-id="element-1165">
                  <label className="block text-[#00E4BB] mb-2 text-sm" data-id="element-1166">
                    Preferred Location
                  </label>
                  <select className="w-full bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-1167">
                    <option value={userProfile.location} data-id="element-1168">
                      {userProfile.location || 'Select location'}
                    </option>
                    <option value="Eco Café" data-id="element-1169">Eco Café</option>
                    <option value="Fitness Center" data-id="element-1170">Fitness Center</option>
                    <option value="Hydration Lounge" data-id="element-1171">Hydration Lounge</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3" data-id="element-1172">
              <Button variant="outline" className="border-[#00E4BB]/30 text-[#00E4BB]" onClick={() => setShowSettingsModal(false)} data-id="element-1173">
                Cancel
              </Button>
              <Button className="bg-[#00E4BB] text-[#1A237E] border-none" onClick={() => setShowSettingsModal(false)} data-id="element-1174">
                Save Changes
              </Button>
            </div>
          </div>
        </div>}
      {/* Notifications Modal */}
      {showNotificationsModal && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-1175">
          <div className="bg-[#1A237E] rounded-xl p-5 w-full max-w-md border border-[#00E4BB]/30" data-id="element-1176">
            <div className="flex justify-between items-center mb-4" data-id="element-1177">
              <h3 className="text-xl font-bold text-white" data-id="element-1178">Notifications</h3>
              <button className="text-gray-400 hover:text-white" onClick={() => setShowNotificationsModal(false)} data-id="element-1179">
                <XIcon className="w-5 h-5" data-id="element-1180" />
              </button>
            </div>
            <div className="space-y-4 mb-6" data-id="element-1181">
              {[{
            title: 'New Berry Blast Flavor Available!',
            message: 'Try our new Berry Blast flavor at your preferred location.',
            time: '2 hours ago',
            isNew: true
          }, {
            title: 'Hydration Goal Achieved!',
            message: "Congratulations! You've reached your daily hydration goal.",
            time: 'Yesterday',
            isNew: true
          }, {
            title: 'Friend Request Accepted',
            message: 'Sarah K. accepted your friend request.',
            time: 'Yesterday',
            isNew: true
          }, {
            title: 'Weekly Hydration Report',
            message: 'Your weekly hydration report is now available.',
            time: '3 days ago',
            isNew: false
          }].map((notification, index) => <GlassCard key={index} className="p-3" data-id="element-1182">
                  <div className="flex items-start" data-id="element-1183">
                    <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3 mt-1" data-id="element-1184">
                      <BellIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1185" />
                    </div>
                    <div className="flex-1" data-id="element-1186">
                      <div className="flex items-center" data-id="element-1187">
                        <h4 className="text-white font-medium" data-id="element-1188">
                          {notification.title}
                        </h4>
                        {notification.isNew && <span className="ml-2 w-2 h-2 bg-[#A6FF4D] rounded-full" data-id="element-1189"></span>}
                      </div>
                      <p className="text-sm text-gray-300 mt-1" data-id="element-1190">
                        {notification.message}
                      </p>
                      <p className="text-xs text-[#00E4BB] mt-2" data-id="element-1191">
                        {notification.time}
                      </p>
                    </div>
                  </div>
                </GlassCard>)}
            </div>
            <div className="space-y-3 mb-6" data-id="element-1192">
              <h4 className="text-[#00E4BB] font-medium mb-2" data-id="element-1193">
                Notification Settings
              </h4>
              <div className="flex items-center justify-between" data-id="element-1194">
                <div className="flex items-center" data-id="element-1195">
                  <div className="w-8 h-8 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-1196">
                    <BellIcon className="w-4 h-4 text-[#00E4BB]" data-id="element-1197" />
                  </div>
                  <span className="text-white text-sm" data-id="element-1198">Push Notifications</span>
                </div>
                <div className="w-10 h-5 bg-[#00E4BB] rounded-full p-0.5 cursor-pointer" onClick={e => {
              const toggle = e.currentTarget.querySelector('div');
              if (toggle) {
                if (toggle.classList.contains('translate-x-5')) {
                  toggle.classList.remove('translate-x-5');
                  toggle.classList.add('translate-x-0');
                  e.currentTarget.classList.remove('bg-[#00E4BB]');
                  e.currentTarget.classList.add('bg-gray-300');
                } else {
                  toggle.classList.remove('translate-x-0');
                  toggle.classList.add('translate-x-5');
                  e.currentTarget.classList.remove('bg-gray-300');
                  e.currentTarget.classList.add('bg-[#00E4BB]');
                }
              }
            }} data-id="element-1199">
                  <div className="w-4 h-4 bg-white rounded-full transform translate-x-5 transition-transform duration-200" data-id="element-1200"></div>
                </div>
              </div>
              <div className="flex items-center justify-between" data-id="element-1201">
                <div className="flex items-center" data-id="element-1202">
                  <div className="w-8 h-8 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-1203">
                    <UsersIcon className="w-4 h-4 text-[#00E4BB]" data-id="element-1204" />
                  </div>
                  <span className="text-white text-sm" data-id="element-1205">Friend Requests</span>
                </div>
                <div className="w-10 h-5 bg-[#00E4BB] rounded-full p-0.5 cursor-pointer" onClick={e => {
              const toggle = e.currentTarget.querySelector('div');
              if (toggle) {
                if (toggle.classList.contains('translate-x-5')) {
                  toggle.classList.remove('translate-x-5');
                  toggle.classList.add('translate-x-0');
                  e.currentTarget.classList.remove('bg-[#00E4BB]');
                  e.currentTarget.classList.add('bg-gray-300');
                } else {
                  toggle.classList.remove('translate-x-0');
                  toggle.classList.add('translate-x-5');
                  e.currentTarget.classList.remove('bg-gray-300');
                  e.currentTarget.classList.add('bg-[#00E4BB]');
                }
              }
            }} data-id="element-1206">
                  <div className="w-4 h-4 bg-white rounded-full transform translate-x-5 transition-transform duration-200" data-id="element-1207"></div>
                </div>
              </div>
            </div>
            <Button fullWidth className="bg-[#00E4BB] text-[#1A237E] border-none" onClick={() => setShowNotificationsModal(false)} data-id="element-1208">
              Mark All as Read
            </Button>
          </div>
        </div>}
      {/* Privacy & Data Modal */}
      {showPrivacyModal && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-1209">
          <div className="bg-[#1A237E] rounded-xl p-5 w-full max-w-md border border-[#00E4BB]/30" data-id="element-1210">
            <div className="flex justify-between items-center mb-4" data-id="element-1211">
              <h3 className="text-xl font-bold text-white" data-id="element-1212">Privacy & Data</h3>
              <button className="text-gray-400 hover:text-white" onClick={() => setShowPrivacyModal(false)} data-id="element-1213">
                <XIcon className="w-5 h-5" data-id="element-1214" />
              </button>
            </div>
            <div className="space-y-4 mb-6" data-id="element-1215">
              <GlassCard className="p-4 cursor-pointer" data-id="element-1216">
                <div className="flex items-center" data-id="element-1217">
                  <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-1218">
                    <LockIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1219" />
                  </div>
                  <div className="flex-1" data-id="element-1220">
                    <h4 className="text-white font-medium" data-id="element-1221">Account Privacy</h4>
                    <p className="text-sm text-gray-300" data-id="element-1222">
                      Control who can see your profile
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1223" />
                </div>
              </GlassCard>
              <GlassCard className="p-4 cursor-pointer" data-id="element-1224">
                <div className="flex items-center" data-id="element-1225">
                  <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-1226">
                    <KeyIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1227" />
                  </div>
                  <div className="flex-1" data-id="element-1228">
                    <h4 className="text-white font-medium" data-id="element-1229">
                      Password & Security
                    </h4>
                    <p className="text-sm text-gray-300" data-id="element-1230">
                      Update your login credentials
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1231" />
                </div>
              </GlassCard>
              <GlassCard className="p-4 cursor-pointer" data-id="element-1232">
                <div className="flex items-center" data-id="element-1233">
                  <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-1234">
                    <EyeIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1235" />
                  </div>
                  <div className="flex-1" data-id="element-1236">
                    <h4 className="text-white font-medium" data-id="element-1237">
                      Activity Visibility
                    </h4>
                    <p className="text-sm text-gray-300" data-id="element-1238">
                      Manage what others can see about your activity
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1239" />
                </div>
              </GlassCard>
              <GlassCard className="p-4 cursor-pointer" data-id="element-1240">
                <div className="flex items-center" data-id="element-1241">
                  <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-1242">
                    <FileTextIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1243" />
                  </div>
                  <div className="flex-1" data-id="element-1244">
                    <h4 className="text-white font-medium" data-id="element-1245">Privacy Policy</h4>
                    <p className="text-sm text-gray-300" data-id="element-1246">
                      Read our privacy policy
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1247" />
                </div>
              </GlassCard>
              <GlassCard className="p-4 cursor-pointer" data-id="element-1248">
                <div className="flex items-center" data-id="element-1249">
                  <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-1250">
                    <DownloadIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1251" />
                  </div>
                  <div className="flex-1" data-id="element-1252">
                    <h4 className="text-white font-medium" data-id="element-1253">
                      Download Your Data
                    </h4>
                    <p className="text-sm text-gray-300" data-id="element-1254">
                      Get a copy of your personal data
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1255" />
                </div>
              </GlassCard>
              <GlassCard className="p-4 cursor-pointer" data-id="element-1256">
                <div className="flex items-center" data-id="element-1257">
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center mr-3" data-id="element-1258">
                    <TrashIcon className="w-5 h-5 text-red-500" data-id="element-1259" />
                  </div>
                  <div className="flex-1" data-id="element-1260">
                    <h4 className="text-red-400 font-medium" data-id="element-1261">Delete Account</h4>
                    <p className="text-sm text-gray-300" data-id="element-1262">
                      Permanently delete your account and data
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-red-400" data-id="element-1263" />
                </div>
              </GlassCard>
            </div>
            <Button fullWidth className="bg-[#00E4BB] text-[#1A237E] border-none" onClick={() => setShowPrivacyModal(false)} data-id="element-1264">
              Close
            </Button>
          </div>
        </div>}
      {/* Help & Support Modal */}
      {showHelpModal && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-1265">
          <div className="bg-[#1A237E] rounded-xl p-5 w-full max-w-md border border-[#00E4BB]/30" data-id="element-1266">
            <div className="flex justify-between items-center mb-4" data-id="element-1267">
              <h3 className="text-xl font-bold text-white" data-id="element-1268">Help & Support</h3>
              <button className="text-gray-400 hover:text-white" onClick={() => setShowHelpModal(false)} data-id="element-1269">
                <XIcon className="w-5 h-5" data-id="element-1270" />
              </button>
            </div>
            <div className="space-y-4 mb-6" data-id="element-1271">
              <GlassCard className="p-4 cursor-pointer" data-id="element-1272">
                <div className="flex items-center" data-id="element-1273">
                  <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-1274">
                    <InfoIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1275" />
                  </div>
                  <div className="flex-1" data-id="element-1276">
                    <h4 className="text-white font-medium" data-id="element-1277">FAQs</h4>
                    <p className="text-sm text-gray-300" data-id="element-1278">
                      Find answers to common questions
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1279" />
                </div>
              </GlassCard>
              <GlassCard className="p-4 cursor-pointer" data-id="element-1280">
                <div className="flex items-center" data-id="element-1281">
                  <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-1282">
                    <MessageSquareIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1283" />
                  </div>
                  <div className="flex-1" data-id="element-1284">
                    <h4 className="text-white font-medium" data-id="element-1285">Contact Support</h4>
                    <p className="text-sm text-gray-300" data-id="element-1286">
                      Get help from our team
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1287" />
                </div>
              </GlassCard>
              <GlassCard className="p-4 cursor-pointer" data-id="element-1288">
                <div className="flex items-center" data-id="element-1289">
                  <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-1290">
                    <AlertTriangleIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1291" />
                  </div>
                  <div className="flex-1" data-id="element-1292">
                    <h4 className="text-white font-medium" data-id="element-1293">Report an Issue</h4>
                    <p className="text-sm text-gray-300" data-id="element-1294">
                      Let us know if something isn't working
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1295" />
                </div>
              </GlassCard>
              <GlassCard className="p-4 cursor-pointer" data-id="element-1296">
                <div className="flex items-center" data-id="element-1297">
                  <div className="w-10 h-10 rounded-full bg-[#00E4BB]/20 flex items-center justify-center mr-3" data-id="element-1298">
                    <UserIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1299" />
                  </div>
                  <div className="flex-1" data-id="element-1300">
                    <h4 className="text-white font-medium" data-id="element-1301">Tutorial</h4>
                    <p className="text-sm text-gray-300" data-id="element-1302">
                      Learn how to use Smart Soda
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-[#00E4BB]" data-id="element-1303" />
                </div>
              </GlassCard>
              <div className="p-4 bg-[#00E4BB]/10 rounded-lg" data-id="element-1304">
                <h4 className="text-[#00E4BB] font-medium mb-2" data-id="element-1305">
                  Contact Information
                </h4>
                <p className="text-white text-sm mb-1" data-id="element-1306">
                  Email: support@smartsoda.com
                </p>
                <p className="text-white text-sm mb-1" data-id="element-1307">Phone: (555) 123-4567</p>
                <p className="text-white text-sm" data-id="element-1308">Hours: 24/7 Support</p>
              </div>
            </div>
            <Button fullWidth className="bg-[#00E4BB] text-[#1A237E] border-none" onClick={() => setShowHelpModal(false)} data-id="element-1309">
              Close
            </Button>
          </div>
        </div>}
      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-1310">
          <div className="bg-[#1A237E] rounded-xl p-5 w-full max-w-sm border border-[#00E4BB]/30" data-id="element-1311">
            <div className="flex flex-col items-center mb-6" data-id="element-1312">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4" data-id="element-1313">
                <LogOutIcon className="w-8 h-8 text-red-500" data-id="element-1314" />
              </div>
              <h3 className="text-xl font-bold text-white text-center" data-id="element-1315">
                Log Out
              </h3>
              <p className="text-gray-300 text-center mt-2" data-id="element-1316">
                Are you sure you want to log out from Smart Soda?
              </p>
            </div>
            <div className="flex space-x-3" data-id="element-1317">
              <Button variant="outline" className="flex-1 border-[#00E4BB]/30 text-[#00E4BB]" onClick={() => setShowLogoutConfirm(false)} data-id="element-1318">
                Cancel
              </Button>
              <Button className="flex-1 bg-red-500 text-white border-none" onClick={() => {
            // Handle logout functionality here
            setShowLogoutConfirm(false);
          }} data-id="element-1319">
                Log Out
              </Button>
            </div>
          </div>
        </div>}
    </div>;
};
const PlusIcon = ({
  className
}: {
  className?: string;
}) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-1320">
    <path d="M5 12h14" data-id="element-1321" />
    <path d="M12 5v14" data-id="element-1322" />
  </svg>;
export default Profile;